import { Injectable, inject, signal } from '@angular/core';
import { NOTIFIER } from '@shared/notifier/types';
import {
  IChingGeneral,
  IChingHexagram,
  IChingHexagramCommentary,
  IChingIndexEntry,
  IChingSourceId,
  IChingTrigramId,
} from '../core/iching.models';
import { HdIChingContentService } from '../hd-iching-content.service';
import { IChingTrigramName } from '../reader/hd-iching-hexagram-reader.component';
import { injectIChingTranslate } from './iching-consult-translate';

/** Wat de duiding moet laden: verandert alleen bij een andere worp of bron. */
export interface IChingReadingContentKey {
  readonly source: IChingSourceId;
  readonly primary: number;
  readonly changed: number | undefined;
  readonly nuclear: number;
}

export function sameReadingContentKey(a: IChingReadingContentKey | undefined, b: IChingReadingContentKey | undefined): boolean {
  if (a === undefined || b === undefined) {
    return a === b;
  }
  return a.source === b.source && a.primary === b.primary && a.changed === b.changed && a.nuclear === b.nuclear;
}

/**
 * Content van fase 3 voor één worp en bron: hexagram en omslag (nodig voor de tekst), index
 * (titel van het kernhexagram) en trigramnamen (reader-kop, optioneel), en Boek III op verzoek.
 * Elke `load` verhoogt de generatie: een oudere load (taalwissel, andere worp) zet niets meer.
 * Op componentniveau geleverd (`providers` van de raadpleegcomponent).
 */
@Injectable()
export class HdIChingReadingContentStore {
  private readonly content = inject(HdIChingContentService);
  private readonly notifier = inject(NOTIFIER);
  private readonly t = injectIChingTranslate();

  private readonly hexagramState = signal<IChingHexagram | undefined>(undefined);
  private readonly changedHexagramState = signal<IChingHexagram | undefined>(undefined);
  private readonly nuclearEntryState = signal<IChingIndexEntry | undefined>(undefined);
  private readonly trigramNamesState = signal<Readonly<Record<IChingTrigramId, IChingTrigramName>> | undefined>(undefined);
  private readonly commentaryState = signal<IChingHexagramCommentary | undefined>(undefined);
  private readonly commentaryBusyState = signal(false);

  readonly hexagram = this.hexagramState.asReadonly();
  readonly changedHexagram = this.changedHexagramState.asReadonly();
  readonly nuclearEntry = this.nuclearEntryState.asReadonly();
  readonly trigramNames = this.trigramNamesState.asReadonly();
  readonly commentary = this.commentaryState.asReadonly();
  readonly commentaryBusy = this.commentaryBusyState.asReadonly();

  private key: IChingReadingContentKey | undefined;
  private generation = 0;
  private commentaryGeneration = 0;

  /**
   * Laadt de content voor deze worp; `undefined` wist ze. Boek III vervalt bij elke nieuwe load.
   * Bij een andere worp verdwijnt de oude tekst meteen (loader); bij enkel een bronwissel blijft
   * hij staan tot de nieuwe er is.
   */
  async load(key: IChingReadingContentKey | undefined): Promise<void> {
    const generation = ++this.generation;
    const isCurrent = (): boolean => generation === this.generation;
    this.key = key;
    this.commentaryGeneration++;
    this.commentaryState.set(undefined);
    this.commentaryBusyState.set(false);
    if (key === undefined) {
      this.hexagramState.set(undefined);
      this.changedHexagramState.set(undefined);
      this.nuclearEntryState.set(undefined);
      return;
    }
    if (this.hexagramState()?.number !== key.primary) {
      this.hexagramState.set(undefined);
    }
    if (this.changedHexagramState()?.number !== key.changed) {
      this.changedHexagramState.set(undefined);
    }

    void this.content
      .loadIndex(key.source)
      .then(index => isCurrent() && this.nuclearEntryState.set(index.find(entry => entry.number === key.nuclear)))
      .catch(() => isCurrent() && this.nuclearEntryState.set(undefined));
    void this.content
      .loadGeneral(key.source)
      .then(general => isCurrent() && this.trigramNamesState.set(toTrigramNames(general)))
      .catch(() => isCurrent() && this.trigramNamesState.set(undefined));

    try {
      const [hexagram, changed] = await Promise.all([
        this.content.loadHexagram(key.primary, key.source),
        key.changed === undefined ? Promise.resolve(undefined) : this.content.loadHexagram(key.changed, key.source),
      ]);
      if (isCurrent()) {
        this.hexagramState.set(hexagram);
        this.changedHexagramState.set(changed);
      }
    } catch {
      if (isCurrent()) {
        this.notifier.error(this.t('humanDesign.iching.panel.loadError'));
      }
    }
  }

  /** Boek III van het primaire hexagram, op verzoek; een nieuwe `load` maakt hem stale. */
  async loadCommentary(): Promise<void> {
    const key = this.key;
    if (key === undefined || this.commentaryBusyState()) {
      return;
    }
    const generation = ++this.commentaryGeneration;
    this.commentaryBusyState.set(true);
    try {
      const commentary = await this.content.loadCommentary(key.primary, key.source);
      if (generation === this.commentaryGeneration) {
        this.commentaryState.set(commentary);
      }
    } catch {
      if (generation === this.commentaryGeneration) {
        this.notifier.error(this.t('humanDesign.iching.panel.commentaryError'));
      }
    } finally {
      if (generation === this.commentaryGeneration) {
        this.commentaryBusyState.set(false);
      }
    }
  }
}

/** Legge kent geen trigramnaam: dan de Chinese naam (zelfde mapping als het paneel). */
function toTrigramNames(general: IChingGeneral): Readonly<Record<IChingTrigramId, IChingTrigramName>> {
  const names = {} as Record<IChingTrigramId, IChingTrigramName>;
  for (const trigram of general.trigrams) {
    names[trigram.id] = { name: trigram.name ?? trigram.chineseName, image: trigram.image };
  }
  return names;
}
